import {
  LocalSocialManPost,
  SocialManNetworks,
} from "@/app/SocialManmodeltypings";
import {
  LocalModelInputPreset,
  LocalReactionType,
  LocalTaskPrivacyLevels,
  LocalUser,
  LocalUserThirdPartyKeys,
} from "@/modeltypings";
import { InternalSubscriptionPriceID } from "@/typings";
import { IBackendManagerInterface } from "@/utils/BackendManager";
import { StripeErrorCodes } from "@mltask/core/ErrorHandling/StripeErrors";
import { DefaultError, useMutation } from "@tanstack/react-query";
var BackendManager: IBackendManagerInterface;
import("@/utils/BackendManager").then((lib) => {
  BackendManager = lib.default;
});

type onMutateCallback<TVariables = unknown, TContext = unknown> = (
  variables: TVariables
) => Promise<TContext | undefined> | TContext | undefined;

type onSuccessCallback<
  TData = unknown,
  TVariables = unknown,
  TContext = unknown
> = (
  data: TData,
  variables: TVariables,
  context: TContext
) => Promise<unknown> | unknown;

type onErrorCallback<
  TError = DefaultError,
  TVariables = unknown,
  TContext = unknown
> = (
  error: TError,
  variables: TVariables,
  context: TContext | undefined
) => Promise<unknown> | unknown;

type CreateSocialManPostVariables = {
  title: string;
  description: string;
  userID: string;
  fileData: string;
  progressCallback?: (progress: any) => void;
};

export function useCreatePostMutation<
  TData = LocalSocialManPost,
  TError = DefaultError,
  TVariables extends CreateSocialManPostVariables = CreateSocialManPostVariables,
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: ["createPost"],
    mutationFn: async (variables: TVariables) => {
      return BackendManager.createSocialManPost(
        variables.title,
        variables.description,
        variables.userID,
        variables.fileData,
        variables.progressCallback
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useCashOutMutation<
  TData = void,
  TError = StripeErrorCodes,
  TVariables extends {
    creditToRedeem: number;
  } = {
    creditToRedeem: number;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.cashOut(variables.creditToRedeem) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useStripeConnectCreateAccountMutation<
  TData = string,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.createStripeConnectAccount() as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useStripeConnectDeleteAccountMutation<
  TData = void,
  TError = DefaultError,
  TVariables = void,
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: ["createPost"],
    mutationFn: async (variables: TVariables) => {
      return BackendManager.deleteStripeConnectAccount() as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useDeletePostMutation<
  TData = LocalSocialManPost,
  TError = DefaultError,
  TVariables = string,
  TContext = unknown
>(
  onMutate?: (
    postID: TVariables
  ) => Promise<TContext | undefined> | TContext | undefined,
  onSuccess?: (
    data: TData,
    postID: TVariables,
    context: TContext
  ) => Promise<unknown> | unknown,
  onError?: (
    error: TError,
    postID: TVariables,
    context: TContext | undefined
  ) => Promise<unknown> | unknown
) {
  return useMutation<TData, TError, TVariables, TContext>({
    // mutationKey: ["deletePost"],
    mutationFn: async (postID: TVariables) => {
      return BackendManager.deleteSocialManPost(
        postID as string
      ) as Promise<TData>;
    },
    onMutate: async (postID) => {
      return await onMutate?.(postID);
    },
    onSuccess: (data, postID, context) => {
      return onSuccess?.(data, postID, context as TContext);
    },
    onError: (error, postID, context) => {
      return onError?.(error as TError, postID, context);
    },
  });
}

export function useRevokeTokenMutation<
  TData = string,
  TError = DefaultError,
  TVariables extends {
    token: string;
    userID: string;
  } = { token: string; userID: string },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: ["revokeComfyToken"],
    mutationFn: async (variables: TVariables) => {
      return BackendManager.revokeComfyToken(
        variables.token,
        variables.userID
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useCreateComfyTokenMutation<
  TData = string,
  TError = DefaultError,
  TVariables extends {
    userID: string;
    password: string;
  } = {
    userID: string;
    password: string;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: ["createComfyToken"],
    mutationFn: async (variables: TVariables) => {
      return BackendManager.createComfyToken(
        variables.userID,
        btoa(variables.password),
        ["create_post", "submit_post"]
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useUpdateUserSubscriptionMutation<
  TData = { url: string },
  TError = DefaultError,
  TVariables extends {
    internalPriceID: InternalSubscriptionPriceID;
  } = {
    internalPriceID: InternalSubscriptionPriceID;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: ["updateUserPlan"],
    mutationFn: async (variables: TVariables) => {
      return BackendManager.stripeSubscriptionCheckout(
        variables.internalPriceID as InternalSubscriptionPriceID
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useUpdateUserUsernameMutation<
  TData = LocalUser,
  TError = any,
  TVariables extends {
    username: string;
  } = {
    username: string;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: ["updateUserUsername"],
    mutationFn: async (variables: TVariables) => {
      return BackendManager.updateUserUsername(
        variables.username
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useUpdateKeysMutation<
  TData = void,
  TError = DefaultError,
  TVariables extends {
    userID: string;
    newKeys: LocalUserThirdPartyKeys;
  } = {
    userID: string;
    newKeys: LocalUserThirdPartyKeys;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: ["updateUserAPIKey"],
    mutationFn: async (variables: TVariables) => {
      return BackendManager.updateUserThirdPartyKey(
        variables.userID,
        variables.newKeys
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function usePostEverywhereMutation<
  TData = void,
  TError = DefaultError,
  TVariables extends {
    userID: string;
    postID: string;
  } = {
    userID: string;
    postID: string;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationKey: ["postEverywhere"],
    mutationFn: async (variables: TVariables) => {
      return BackendManager.postSocialManPostToEverywhere(
        variables.userID,
        variables.postID
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useUpdatePostMutation<
  TData = void,
  TError = DefaultError,
  TVariables extends { localSocialManPost: LocalSocialManPost } = {
    localSocialManPost: LocalSocialManPost;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.updateSocialManPost(
        variables.localSocialManPost
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useSignOutOfNetworkMutation<
  TData = void,
  TError = DefaultError,
  TVariables extends {
    network: SocialManNetworks;
  } = {
    network: SocialManNetworks;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.updateUserRemoveNetworkConnection(
        variables.network
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

type UpdateUserPresetVariables = {
  preset: LocalModelInputPreset;
  presetID: string;
  presetName?: string;
  description?: string;
  isPublic?: boolean;
  isOpenSource?: boolean;
};

export function useUpdateUserPresetForModelMutation<
  TData = LocalModelInputPreset,
  TError = DefaultError,
  TVariables extends UpdateUserPresetVariables = UpdateUserPresetVariables,
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.updateUserPresetForModel(
        variables.preset,
        variables.presetID,
        variables.presetName,
        variables.description,
        variables.isPublic,
        variables.isOpenSource
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useDeleteUserPresetWithPresetIDMutation<
  TData = string,
  TError = DefaultError,
  TVariables extends {
    presetID: string;
  } = {
    presetID: string;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.deleteUserPresetWithPresetID(
        variables.presetID
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

type AddUserReactionToTaskVariables = {
  userID: string;
  taskID: string;
  reaction: LocalReactionType;
};

export function useAddUserReactionToTaskMutation<
  TData = boolean,
  TError = DefaultError,
  TVariables extends AddUserReactionToTaskVariables = AddUserReactionToTaskVariables,
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.addUserReactionToTask(
        variables.userID,
        variables.taskID,
        variables.reaction
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useUpdateTaskExecutionResultPrivacyMutation<
  TData = boolean,
  TError = DefaultError,
  TVariables extends {
    taskID: string;
    newPrivacyLevel: LocalTaskPrivacyLevels;
  } = {
    taskID: string;
    newPrivacyLevel: LocalTaskPrivacyLevels;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.updateTaskExecutionResultsPrivacy(
        variables.taskID,
        variables.newPrivacyLevel
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}

export function useDeleteTaskExecutionResultMutation<
  TData = boolean | undefined,
  TError = DefaultError,
  TVariables extends {
    taskID: string;
  } = {
    taskID: string;
  },
  TContext = unknown
>(
  onMutate?: onMutateCallback<TVariables, TContext>,
  onSuccess?: onSuccessCallback<TData, TVariables, TContext>,
  onError?: onErrorCallback<TError, TVariables, TContext | undefined>
) {
  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables: TVariables) => {
      return BackendManager.deleteUserTaskResultWithTaskID(
        variables.taskID
      ) as Promise<TData>;
    },
    onMutate,
    onSuccess,
    onError,
  });
}
